import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

interface Activity {
  _id: string;
  contact_id: string;
  type: 'primo_contatto' | 'risposta' | 'presentazione_aziendale' | 'brief' | 'presentazione_progetto' | 'esito';
  outcome: 'positivo' | 'negativo' | 'in_corso';
  notes: string;
  created_at: string;
  updated_at: string;
}

interface ActivityTimelineManagementProps {
  contactId: string;
  onActivityUpdate?: () => void;
}

const TimelineContainer = styled.div`
  background: white;
  border-radius: 12px;
  padding: 30px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  margin-top: 30px;
`;

const TimelineTitle = styled.h2`
  color: #1f2937;
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0 0 20px 0;
`;

const AddActivityButton = styled.button`
  background: #667eea;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 12px 20px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  margin-bottom: 20px;
  transition: background-color 0.2s;
  
  &:hover {
    background: #5a67d8;
  }
`;

const ActivityForm = styled.div<{ isVisible: boolean }>`
  display: ${props => props.isVisible ? 'block' : 'none'};
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const FormLabel = styled.label`
  display: block;
  color: #374151;
  font-size: 0.875rem;
  font-weight: 500;
  margin-bottom: 6px;
`;

const FormSelect = styled.select`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  background: white;
  color: #1f2937;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const FormTextarea = styled.textarea`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  background: white;
  color: #1f2937;
  min-height: 80px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const FormActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

const FormButton = styled.button<{ variant?: 'primary' | 'secondary' }>`
  background: ${props => props.variant === 'primary' ? '#667eea' : '#f3f4f6'};
  color: ${props => props.variant === 'primary' ? 'white' : '#374151'};
  border: 1px solid ${props => props.variant === 'primary' ? '#667eea' : '#d1d5db'};
  border-radius: 8px;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: ${props => props.variant === 'primary' ? '#5a67d8' : '#e5e7eb'};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const TimelineList = styled.div`
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    left: 20px;
    top: 0;
    bottom: 0;
    width: 2px;
    background: #e5e7eb;
  }
`;

const TimelineItem = styled.div`
  position: relative;
  padding-left: 60px;
  margin-bottom: 24px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const TimelineIcon = styled.div<{ outcome: string }>`
  position: absolute;
  left: 12px;
  top: 8px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: ${props => {
    switch (props.outcome) {
      case 'positivo': return '#10b981';
      case 'negativo': return '#ef4444';
      case 'in_corso': return '#f59e0b';
      default: return '#6b7280';
    }
  }};
`;

const ActivityCard = styled.div`
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;
`;

const ActivityHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const ActivityType = styled.span`
  font-weight: 600;
  color: #1f2937;
  font-size: 0.875rem;
`;

const OutcomeChip = styled.span<{ outcome: string }>`
  display: inline-block;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
  margin-left: 8px;
  background: ${props => {
    switch (props.outcome) {
      case 'positivo': return '#d1fae5';
      case 'negativo': return '#fee2e2';
      case 'in_corso': return '#fef3c7';
      default: return '#f3f4f6';
    }
  }};
  color: ${props => {
    switch (props.outcome) {
      case 'positivo': return '#065f46';
      case 'negativo': return '#991b1b';
      case 'in_corso': return '#92400e';
      default: return '#374151';
    }
  }};
`;

const ActivityActions = styled.div`
  display: flex;
  gap: 8px;
`;

const ActionButton = styled.button`
  background: none;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 0.75rem;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: #f3f4f6;
    color: #374151;
  }
`;

const ActivityDate = styled.div`
  color: #6b7280;
  font-size: 0.75rem;
  margin-bottom: 8px;
`;

const ActivityNotes = styled.div`
  color: #4b5563;
  font-size: 0.875rem;
  line-height: 1.5;
`;

const EmptyState = styled.div`
  text-align: center;
  color: #6b7280;
  font-style: italic;
  padding: 40px 20px;
`;

const ErrorMessage = styled.div`
  background: #fee2e2;
  color: #991b1b;
  padding: 12px;
  border-radius: 8px;
  margin-bottom: 16px;
  font-size: 0.875rem;
`;

const SuccessMessage = styled.div`
  background: #d1fae5;
  color: #065f46;
  padding: 12px;
  border-radius: 8px;
  margin-bottom: 16px;
  font-size: 0.875rem;
`;

const ActivityTimelineManagement: React.FC<ActivityTimelineManagementProps> = ({ contactId, onActivityUpdate }) => {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingActivity, setEditingActivity] = useState<Activity | null>(null);
  const [formData, setFormData] = useState({
    type: '' as Activity['type'],
    outcome: '' as Activity['outcome'],
    notes: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const fetchActivities = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/activities/contact/${contactId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setActivities(data);
      } else {
        setError('Impossibile recuperare le attività');
      }
    } catch (err) {
      setError('Errore durante il recupero delle attività');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchActivities();
  }, [contactId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      const url = editingActivity 
        ? `/api/activities/${editingActivity._id}` 
        : `/api/activities/contact/${contactId}`;
      const method = editingActivity ? 'PUT' : 'POST';
      
      // Don't include contact_id in body - it's now in the URL for security
      const body = formData;

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });

      // Check if response is successful (200-299 status codes)
      if (response.status >= 200 && response.status < 300) {
        const savedActivity = await response.json();
        
        // Update local activities state instead of refetching
        if (editingActivity) {
          // Update existing activity
          setActivities(prev => prev.map(activity => 
            activity._id === editingActivity._id ? savedActivity : activity
          ));
        } else {
          // Add new activity to the beginning of the list
          setActivities(prev => [savedActivity, ...prev]);
        }
        
        setSuccess(editingActivity ? 'Attività aggiornata con successo!' : 'Attività creata con successo!');
        setFormData({ type: '' as Activity['type'], outcome: '' as Activity['outcome'], notes: '' });
        setShowForm(false);
        setEditingActivity(null);
        
        // Call parent update callback
        if (onActivityUpdate) {
          onActivityUpdate();
        }
      } else {
        // Try to parse error message, but don't fail if parsing fails
        let errorMessage = 'Impossibile salvare l\'attività';
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorMessage;
        } catch (parseError) {
          console.warn('Could not parse error response:', parseError);
        }
        setError(errorMessage);
      }
    } catch (err) {
      console.error('Network or other error:', err);
      setError('Errore di rete. Riprova o ricarica la pagina per verificare se l\'attività è stata salvata.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (activity: Activity) => {
    setEditingActivity(activity);
    setFormData({
      type: activity.type,
      outcome: activity.outcome,
      notes: activity.notes
    });
    setShowForm(true);
    setError('');
    setSuccess('');
  };

  const handleDelete = async (activityId: string) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa attività?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/activities/${activityId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        // Update local activities state instead of refetching
        setActivities(prev => prev.filter(activity => activity._id !== activityId));
        
        setSuccess('Attività eliminata con successo!');
        if (onActivityUpdate) {
          onActivityUpdate();
        }
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Impossibile eliminare l\'attività');
      }
    } catch (err) {
      setError('Errore durante l\'eliminazione dell\'attività');
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingActivity(null);
    setFormData({ type: '' as Activity['type'], outcome: '' as Activity['outcome'], notes: '' });
    setError('');
    setSuccess('');
  };

  const getActivityTypeLabel = (type: string): string => {
    const labels: Record<string, string> = {
      'primo_contatto': 'Primo contatto',
      'risposta': 'Azione',
      'presentazione_aziendale': 'Presentazione Aziendale',
      'brief': 'Brief',
      'presentazione_progetto': 'Presentazione Progetto',
      'esito': 'Esito'
    };
    return labels[type] || type;
  };

  const getOutcomeLabel = (outcome: string): string => {
    const labels: Record<string, string> = {
      'nuovo': 'Nuovo',
      'positivo': 'Positivo',
      'negativo': 'Negativo',
      'in_corso': 'In corso'
    };
    return labels[outcome] || outcome;
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('it-IT', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <TimelineContainer>
      <TimelineTitle>Timeline Attività</TimelineTitle>
      
      <AddActivityButton onClick={() => setShowForm(!showForm)}>
        {showForm ? 'Annulla' : 'Aggiungi Attività'}
      </AddActivityButton>

      {error && <ErrorMessage>{error}</ErrorMessage>}
      {success && <SuccessMessage>{success}</SuccessMessage>}

      <ActivityForm isVisible={showForm}>
        <form onSubmit={handleSubmit}>
          <FormGroup>
            <FormLabel>Tipo di Attività</FormLabel>
            <FormSelect
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value as Activity['type'] })}
              required
            >
              <option value="">Seleziona tipo</option>
              <option value="primo_contatto">Primo contatto</option>
              <option value="risposta">Azione</option>
              <option value="presentazione_aziendale">Presentazione Aziendale</option>
              <option value="brief">Brief</option>
              <option value="presentazione_progetto">Presentazione Progetto</option>
              <option value="esito">Esito</option>
            </FormSelect>
          </FormGroup>

          <FormGroup>
            <FormLabel>Esito</FormLabel>
            <FormSelect
              value={formData.outcome}
              onChange={(e) => setFormData({ ...formData, outcome: e.target.value as Activity['outcome'] })}
              required
            >
              <option value="">Seleziona esito</option>
              <option value="positivo">Positivo</option>
              <option value="negativo">Negativo</option>
              <option value="in_corso">In corso</option>
            </FormSelect>
          </FormGroup>

          <FormGroup>
            <FormLabel>Note</FormLabel>
            <FormTextarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Aggiungi note sull'attività..."
            />
          </FormGroup>

          <FormActions>
            <FormButton type="button" onClick={handleCancel}>
              Annulla
            </FormButton>
            <FormButton type="submit" variant="primary" disabled={submitting}>
              {submitting ? 'Salvando...' : (editingActivity ? 'Aggiorna' : 'Crea Attività')}
            </FormButton>
          </FormActions>
        </form>
      </ActivityForm>
      
      {loading ? (
        <div>Caricamento attività...</div>
      ) : activities.length === 0 ? (
        <EmptyState>
          Nessuna attività registrata per questo contatto.
        </EmptyState>
      ) : (
        <TimelineList>
          {activities.map((activity) => (
            <TimelineItem key={activity._id}>
              <TimelineIcon outcome={activity.outcome} />
              <ActivityCard>
                <ActivityHeader>
                  <div>
                    <ActivityType>{getActivityTypeLabel(activity.type)}</ActivityType>
                    <OutcomeChip outcome={activity.outcome}>
                      {getOutcomeLabel(activity.outcome)}
                    </OutcomeChip>
                  </div>
                  <ActivityActions>
                    <ActionButton onClick={() => handleEdit(activity)}>
                      Modifica
                    </ActionButton>
                    <ActionButton onClick={() => handleDelete(activity._id)}>
                      Elimina
                    </ActionButton>
                  </ActivityActions>
                </ActivityHeader>
                <ActivityDate>
                  {formatDate(activity.created_at)}
                  {activity.updated_at !== activity.created_at && (
                    <span> (modificato: {formatDate(activity.updated_at)})</span>
                  )}
                </ActivityDate>
                {activity.notes && (
                  <ActivityNotes>
                    <strong>Note:</strong> {activity.notes}
                  </ActivityNotes>
                )}
              </ActivityCard>
            </TimelineItem>
          ))}
        </TimelineList>
      )}
    </TimelineContainer>
  );
};

export default ActivityTimelineManagement;
